import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import OverlayLoading from "../components/OverlayLoading";
import Title from "../components/title";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import html2canvas from "html2canvas";
import SlideBuilder from "../components/CustomerCareKPIPerformance/SlideBuilder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faFileCsv, faUpload } from "@fortawesome/free-solid-svg-icons";
import { apiFetch } from "../apiClient";
import TotalEnquiries from "../components/CustomerCareKPIPerformance/TotalEnquiries";
import AverageTimeToClose from "../components/CustomerCareKPIPerformance/AverageTimeToClose";
import ActivityByMonth from "../components/CustomerCareKPIPerformance/ActivityByMonth";
import TicketsByVertical from "../components/CustomerCareKPIPerformance/TicketByVertical";
import AvgTimeToCloseByVerticalMonth from "../components/CustomerCareKPIPerformance/AvgTimeToCloseByVerticalMonth";

const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const CustomerCareKPIPerformance = () => {
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const chartKeys = [
    "open_tickets",
    "open_tickets_heatmap",
    "ticket_status_distribution_vertical",
    "average_time_closed",
    "work_summary",
    "work_summary_per_person",
  ];

  const chartRefs = useRef(
    chartKeys.reduce((acc, key) => {
      acc[toCamelCase(key)] = React.createRef();
      return acc;
    }, {})
  ).current;
  const radarChartRefs = useRef({});

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("No files selected");
      return;
    }
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      files.forEach((file, idx) => {
        formData.append("files", file);
      });

      const response = await apiFetch(
        `/api/v1/customerCareKPIPerformance/upload`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          errorMessage =
            errorResponse.detail || errorResponse.error || errorMessage;
        } catch (err) {
          console.error("Failed to parse error response", err);
        }
        toast.error(errorMessage);
        return;
      }

      const result = await response.json();
      setDashboardData(result);
      toast.success("Upload successful");
      setFiles([]);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: true,
  });

  const handleExportAllChartsToAPI = async (slideExportData) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();

      const chartEntries = chartKeys.map((key) => ({
        name: key,
        ref: chartRefs[toCamelCase(key)],
      }));

      for (const { name, ref } of chartEntries) {
        if (!ref || !ref.current) {
          console.warn(`Skipping "${name}" - ref not found or not mounted`);
          continue;
        }

        try {
          const canvas = await html2canvas(ref.current, {
            useCORS: true,
            scale: 2,
          });

          if (!canvas) {
            console.error(`html2canvas failed for ${name}`);
            continue;
          }

          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/png")
          );

          if (blob) {
            formData.append("images", blob, `${name}.png`);
          } else {
            console.error(`Failed to create blob for ${name}`);
          }
        } catch (err) {
          console.error(`Error exporting chart "${name}":`, err);
        }
      }

      for (const [owner, radarRef] of Object.entries(radarChartRefs.current)) {
        if (!radarRef?.current) continue;

        const canvas = await html2canvas(radarRef.current, {
          useCORS: true,
          scale: 2,
        });
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );

        if (blob) {
          formData.append("images", blob, `radar_${owner}.png`);
        }
      }

      formData.append("metadata", JSON.stringify(slideExportData));

      const response = await apiFetch(
        `/api/v1/customerCareKPIPerformance/generate`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }

      const contentType = response.headers.get("Content-Type");

      if (
        response.ok &&
        contentType &&
        contentType.includes(
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        )
      ) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-"); // optional
        link.setAttribute("download", `Slide_Presentation_${timestamp}.pptx`);

        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const errorText = await response.text();
        console.error("Download failed. Response not PowerPoint:", errorText);
        toast.error(
          "Upload succeeded, but the server did not return a .pptx file."
        );
      }
    } catch (err) {
      console.error("Error uploading charts:", err);
      toast.error("Failed to upload charts.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "csv":
        return <FontAwesomeIcon icon={faFileCsv} />;
      default:
        return <FontAwesomeIcon icon={faFile} />;
    }
  };

  return (
    <div>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="p-6 pb-0">
        <Title title={"Customer Care KPI Performance"} />
      </div>

      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="CSV"
        />

        {files.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Selected Files ({files.length})
              </h3>
              <div className="text-sm text-gray-500">
                Total:{" "}
                {formatFileSize(
                  files.reduce((acc, file) => acc + file.size, 0)
                )}
              </div>
            </div>
            <ul className="list-disc pl-5">
              {files.map((file, index) => (
                <div
                  key={file.name + index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors mt-3"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)} • CSV File
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </ul>

            <button
              onClick={handleUpload}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" /> Upload All
            </button>
          </div>
        )}
      </div>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboardData?.total_enquiries?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.total_enquiries}
          >
            <TotalEnquiries data={dashboardData.total_enquiries} />
          </div>
        )}
        {dashboardData?.average_time_to_close_monthly?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.average_time_to_close_monthly}
          >
            <AverageTimeToClose
              data={dashboardData.average_time_to_close_monthly}
            />
          </div>
        )}
        {dashboardData?.activity_chart?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.activity_chart}
          >
            <ActivityByMonth data={dashboardData.activity_chart} />
          </div>
        )}
        {dashboardData?.tickets_by_vertical?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.tickets_by_vertical}
          >
            <TicketsByVertical data={dashboardData.tickets_by_vertical} />
          </div>
        )}
        {dashboardData?.avg_time_by_vertical_month?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.avg_time_by_vertical_month}
          >
            <AvgTimeToCloseByVerticalMonth
              data={dashboardData.avg_time_by_vertical_month}
            />
          </div>
        )}
      </div>
      {Object.keys(dashboardData).length > 0 && (
        <SlideBuilder
          onExport={handleExportAllChartsToAPI}
          contents={chartKeys}
        />
      )}
    </div>
  );
};

export default CustomerCareKPIPerformance;
